import type { Post } from '@/@types/post'
import { type StoryFeed, StoryFeedType } from '@/@types/story'
import Page from '@/components/Page'
import PostForm from '@/components/Post/PostForm'
import PostList from '@/components/Post/PostList'
import { Tab } from '@/components/Sidebar/config'
import StoryFeedList from '@/components/Story/feed/StoryFeedList'
import { useStoryViewer } from '@/components/Story/viewer/StoryViewerContext'
import { useScrollToTop } from '@/utils/dom'
import { FOLLOWING_POSTS_QUERY_OPTIONS, type ListPage, cachePost } from '@/utils/fetchers/post'
import {
	FOLLOWING_STORIES_QUERY_OPTIONS,
	readStory,
	removeStoryResourceFromCache,
} from '@/utils/fetchers/story'
import usePageNeedRefreshSubscriber from '@/utils/hooks/usePageNeedRefreshSubscriber'
import usePosts from '@/utils/hooks/usePosts'
import { useCurrentUser } from '@/utils/hooks/useUser'
import { prepareToRefetchListQuery } from '@/utils/list'
import queryClient from '@/utils/queryClient'
import { type InfiniteData, useQuery, useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { cloneDeep, update } from 'lodash-es'
import toast from 'react-hot-toast'
import { useTitle } from 'react-use'
import z from 'zod'
import styles from './following.module.css'

const SearchParamsSchema = z.object({
	id: z.string().optional(),
})

export const Route = createFileRoute('/following')({
	component: FollowingPage,
	loader: async () => {
		await queryClient.ensureInfiniteQueryData(FOLLOWING_POSTS_QUERY_OPTIONS)
	},
	validateSearch: SearchParamsSchema,
})

export default function FollowingPage() {
	useTitle(`动态 - 即刻`)
	const currentUser = useCurrentUser()
	const { openStoryViewer } = useStoryViewer()
	const { data, fetchNextPage, refetch, isFetchingNextPage } = useSuspenseInfiniteQuery(
		FOLLOWING_POSTS_QUERY_OPTIONS
	)
	const {
		data: storyFeeds = [],
		isPending: isStoryFeedPending,
		isError: isStoryFeedError,
		refetch: refetchStoryFeed,
	} = useQuery(FOLLOWING_STORIES_QUERY_OPTIONS)
	const postIds = data.pages.flatMap((page) => page.data)
	const posts = usePosts(postIds)
	const scrollToTop = useScrollToTop()

	usePageNeedRefreshSubscriber(Tab.FOLLOWING_POST, () => {
		scrollToTop()
		prepareToRefetchListQuery(FOLLOWING_POSTS_QUERY_OPTIONS.queryKey)
		Promise.all([refetch(), refetchStoryFeed()]).then(() => {
			toast.success('加载成功')
		})
	})

	const handleStoryItemClick = (feed: StoryFeed) => {
		if (feed.type === StoryFeedType.STORY_RESOURCE) {
			window.open(feed.data.url, '_blank', 'noopener,noreferrer')
			if (feed.data.persistent !== true) {
				removeStoryResourceFromCache(queryClient, feed.data.id)
			}
			void readStory(feed.data.id, 'STORY_RESOURCE').catch(() => undefined)
			return
		}

		const participantUsernames =
			feed.user.username === currentUser.username
				? [currentUser.username]
				: storyFeeds.flatMap((storyFeed) =>
						storyFeed.type === StoryFeedType.USER_STORY &&
						storyFeed.user.username !== currentUser.username
							? [storyFeed.user.username]
							: []
					)

		openStoryViewer({
			type: 'PERSONAL_STORIES',
			username: feed.user.username,
			participantUsernames,
		})
	}

	const handlePostSubmit = (newPost: Post) => {
		queryClient.setQueryData<InfiniteData<ListPage<string>>>(
			FOLLOWING_POSTS_QUERY_OPTIONS.queryKey,
			(oldData) => {
				if (!oldData) return oldData

				const newData = cloneDeep(oldData)
				cachePost(newPost)
				update(newData.pages[0], 'data', (data: Post[]) => [newPost.id, ...data])

				return newData
			}
		)
	}

	return (
		<Page>
			<div className={styles.postForm}>
				<PostForm onSubmit={handlePostSubmit} />
			</div>
			<StoryFeedList
				currentUser={currentUser}
				feeds={storyFeeds}
				loading={isStoryFeedPending}
				failed={isStoryFeedError}
				onItemClick={handleStoryItemClick}
				onRetry={() => void refetchStoryFeed()}
			/>
			<PostList
				data={posts}
				isLoading={isFetchingNextPage}
				onLoadMore={async () => void (await fetchNextPage())}
			/>
		</Page>
	)
}
